import * as React from "react";
import { useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";

import { campaignsKeys, Disposition, useDeleteDisposition } from "@/hooks/api/use-campaigns";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { CreateDisposition } from "./createDisposition";

export function Dispositions(): React.JSX.Element {
	const { campaignId } = useParams();

	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
	const [selectedDisposition, setSelectedDisposition] = useState<Disposition | null>(null);

	const {
		items: dispositions,
		isLoading,
		totalItems,
		refetchData: fetchDispositions,
	} = useCachedTableStateWithUrl<Disposition>({
		queryKey: campaignsKeys.dispositions(campaignId!),
		endpoint: `/campaigns/${campaignId}/dispositions`,
	});

	const {
		data: selectedDispositionToDelete,
		open: isDeleteDispositionModalOpen,
		handleClose: handleDeleteDispositionModalClose,
		handleOpen: handleDeleteDispositionModalOpen,
	} = useDialog<Disposition | null>();

	const deleteDispositionMutation = useDeleteDisposition();

	const handleDeleteDisposition = async () => {
		if (selectedDispositionToDelete?.id) {
			await deleteDispositionMutation.mutateAsync({
				campaignId: campaignId!,
				id: selectedDispositionToDelete.id,
			});
			handleDeleteDispositionModalClose();
		}
	};

	const renderHeaderButtons = () => {
		return (
			<Button
				startIcon={<PlusIcon />}
				variant="contained"
				onClick={() => {
					setSelectedDisposition(null);
					setIsCreateDrawerOpen(true);
				}}
			>
				Create Disposition
			</Button>
		);
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
			},
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
			},

			{
				header: {
					id: "label",
					label: "Label",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "label",
			},

			{
				header: {
					id: "isSystem",
					label: "Is System",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "isSystem",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.isSystem ? "success" : "error"}
							variant="filled"
							size="small"
							label={row.isSystem ? "Yes" : "No"}
						/>
					);
				},
			},

			{
				header: {
					id: "description",
					label: "Description",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "description",
			},

			{
				header: {
					id: "usedForAnalyses",
					label: "Used For Analysis",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "usedForAnalyses",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.usedForAnalyses ? "success" : "error"}
							variant="filled"
							size="small"
							label={row.usedForAnalyses ? "Yes" : "No"}
						/>
					);
				},
			},

      {
				header: {
					id: "sendEmail",
					label: "Send Email",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "sendEmail",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.sendEmail ? "success" : "error"}
							variant="filled"
							size="small"
							label={row.sendEmail ? "Yes" : "No"}
						/>
					);
				},
			},

			{
				header: {
					id: "countsAsConversion",
					label: "Conversion",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "countsAsConversion",
				getCellValue: (row: any) => {
					return (
						<Chip
							color={row.countsAsConversion ? "success" : "error"}
							variant="filled"
							size="small"
							label={row.countsAsConversion ? "Yes" : "No"}
						/>
					);
				},
			},

			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedDisposition(row);
										setIsCreateDrawerOpen(true);
									},
								},

								...(row.isSystem ? [] : [{
									label: "Delete",
										icon: <TrashIcon width={20} height={20} weight="fill" />,
										sx: { color: "error.main" },
										onClick: () => handleDeleteDispositionModalOpen(row),
									}]),
							]}
						/>
					</Box>
				),
			},
		];
	}, [handleDeleteDispositionModalOpen]);

	return (
		<>
			<CustomTable
				columns={columns}
				data={dispositions}
				refetchData={fetchDispositions}
				isLoading={isLoading}
				totalItems={totalItems}
				tableHeaderTitle="Dispositions"
				renderHeaderButtons={renderHeaderButtons}
			/>
			<DeleteModal
				open={isDeleteDispositionModalOpen}
				onClose={handleDeleteDispositionModalClose}
				onDelete={handleDeleteDisposition}
				title="Delete Disposition"
				description="Are you sure you want to delete this disposition?"
			/>
			<CreateDisposition
				campaignId={campaignId!}
				disposition={selectedDisposition}
				isCreateDrawerOpen={isCreateDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateDrawerOpen(false);

					setTimeout(() => {
						setSelectedDisposition(null);
					}, 300);
				}}
			/>
		</>
	);
}
